import React, { useState, useCallback, useEffect, useMemo } from "react"
import styles from "./index.module.css"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { getGuideTrips, getGuideTripsSearch, cleanTrip } from "store/trips"
import { routes } from "router/routes"
// Custom hooks
import { useExperienceSearch } from "./hooks/useExperienceSearch"
import { usePagination } from "./hooks/usePagination"
import { useExperienceFilters } from "./hooks/useExperienceFilters"
import { useTabCounts } from "./hooks/useTabCounts"

// Components
import SearchHeader from "./components/SearchHeader"
import FilterButtons from "./components/FilterButtons"
import ExperienceGrid from "./components/ExperienceGrid"
import { AddNewListing } from "./modal/AddNewListing"
import { useTripSearchLogic } from "hooks/useTripSearchLogic"
import SearchCategory from "pagesV2/pro/Experiences/Home/TripSearchBox"
import FilterDropdown from "./FilterDropdown"
import { useExperiencesActions } from "hooks/useExperiencesActions"
import EditCategoryModal from "./modal/EditCategory"
import { ListingTable } from "./ListingTable"
import { useMediaQuery } from "react-responsive"
export const ExperienceListings = ({ onSearch }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isCompactDevice = useMediaQuery({ query: "(max-width: 1280px)" })

  // Redux state
  const { items: trips, metadata, loading } = useSelector((state) => state.trips)

  // Custom hooks
  const { page, pageSize, handlePageChange, handlePageSizeChange, resetPagination } = usePagination(1, 10)
  const {
    activeTab,
    activeListingTab,
    selectedType,
    isFilterDropdownOpen,
    handleTabChange,
    handleListingTabChange,
    setSelectedType,
    handleFilterIconClick,
    handleCloseFilterDropdown
  } = useExperienceFilters("published")
  const {
    searchQuery,
    confirmedSearchQuery,
    selectedTrip,
    showSuggestions,
    showSearchResults,
    debouncedSearchQuery,
    searchTrips,
    searchLoading,
    handleSearchChange,
    handleSearchKeyDown,
    handleClearSearch,
    handleTripClick,
    setSearchQuery,
    setConfirmedSearchQuery,
    setSelectedTrip,
    setShowSuggestions,
    setShowSearchResults
  } = useExperienceSearch({ pageSize, onSearch })

  // Modal state
  const [isAddNewListingModalOpen, setIsAddNewListingModalOpen] = useState(false)
  const [showPagination, setShowPagination] = useState(true)
  const [originalStatusCount, setOriginalStatusCount] = useState(null)
  const [selectedListing, setSelectedListing] = useState("table")
  const [editCategoryModal, setEditCategoryModal] = useState({ visible: false, trip: null })

  // Experience actions
  const { handleDelete, handleDuplicate, handleDeactivate, handleShare, handlePublish, modal, isDuplicateInProgress, duplicatingTripId } =
    useExperiencesActions()

  // Memoized calculations
  const { categorizedSearchResults } = useTripSearchLogic({
    trips:
      (showSuggestions && debouncedSearchQuery.trim()) || (debouncedSearchQuery.trim().length >= 3 && !confirmedSearchQuery.trim())
        ? searchTrips
        : trips,
    searchQuery,
    selectedTrip,
    debouncedSearchQuery,
    showSuggestions
  })

  const tripsForMainList = useMemo(() => {
    if (selectedTrip) return [selectedTrip]
    if (confirmedSearchQuery.trim()) return searchTrips
    return trips
  }, [selectedTrip, confirmedSearchQuery, searchTrips, trips])

  const { publishedCount, draftCount, unpublishedCount } = useTabCounts(
    selectedTrip,
    confirmedSearchQuery,
    activeListingTab,
    metadata,
    originalStatusCount
  )

  // Preserve original status_count before search API overwrites it
  useEffect(() => {
    if (metadata?.status_count && !searchQuery.trim()) {
      setOriginalStatusCount(metadata.status_count)
    }
  }, [metadata?.status_count, searchQuery])

  const handleClearSearchWithReset = useCallback(() => {
    handleClearSearch()
    resetPagination()
    dispatch(getGuideTrips(null, { q: { status_eq: activeListingTab }, page: 1, per_page: pageSize, sort_by: "updated_at_desc" }))
  }, [handleClearSearch, resetPagination, dispatch, activeListingTab, pageSize])

  const handleTripClickWithAPI = useCallback(
    (trip) => {
      handleTripClick(trip)
      setShowPagination(false)
      handleListingTabChange(trip.status)
      resetPagination()
      dispatch(
        getGuideTripsSearch(null, {
          q: { status_eq: trip.status, title_eq: trip.title },
          page: 1,
          per_page: pageSize,
          sort_by: "updated_at_desc",
          search: false
        })
      )
    },
    [handleTripClick, handleListingTabChange, resetPagination, dispatch, pageSize]
  )

  const handleTabChangeWithReset = useCallback(
    (tab) => {
      resetPagination()
      handleListingTabChange(tab)
      setSelectedTrip(null)
      setSearchQuery("")
      setConfirmedSearchQuery("")
      setShowSuggestions(false)
      setShowSearchResults(false)
      setShowPagination(true)
    },
    [
      resetPagination,
      handleListingTabChange,
      setSelectedTrip,
      setSearchQuery,
      setConfirmedSearchQuery,
      setShowSuggestions,
      setShowSearchResults
    ]
  )

  const onAddNewListing = () => {
    setIsAddNewListingModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsAddNewListingModalOpen(false)
    setSelectedType("")
  }

  const handleContinueModal = (type) => {
    if (!type) return
    setIsAddNewListingModalOpen(false)
    navigate(`${routes.proNewExperienceV2Path({ id: "new" })}?type=${type}`)
  }

  const handleAddFromEmptyState = (type) => {
    if (type) {
      return handleContinueModal(type)
    }
    onAddNewListing()
    return Promise.resolve()
  }

  const handleEditCategory = (trip) => {
    setEditCategoryModal({ visible: true, trip })
  }

  const handleCloseEditCategoryModal = () => {
    setEditCategoryModal({ visible: false, trip: null })
  }

  const handleSaveCategory = (selectedCategory) => {
    console.log("Saving category:", selectedCategory, "for trip:", editCategoryModal.trip)
    handleCloseEditCategoryModal()
    handleActionComplete()
  }

  const handleActionComplete = useCallback(() => {
    // Refresh the current tab data
    dispatch(
      getGuideTrips(null, {
        q: { status_eq: activeListingTab },
        page,
        per_page: pageSize,
        sort_by: "updated_at_desc"
      })
    )
  }, [dispatch, activeListingTab, page, pageSize, i18n])

  // Fetching Trips Based on Tab and Pagination
  useEffect(() => {
    if (!activeListingTab) return
    if (confirmedSearchQuery.trim() || selectedTrip) return

    dispatch(
      getGuideTrips(null, {
        q: { status_eq: activeListingTab },
        page,
        per_page: pageSize,
        sort_by: "updated_at_desc"
      })
    )

    return () => {
      dispatch(cleanTrip())
    }
  }, [dispatch, activeListingTab, page, pageSize, i18n, confirmedSearchQuery, selectedTrip])

  // Fetching Trips on Search (suggestions while typing)
  useEffect(() => {
    if (confirmedSearchQuery.trim()) return
    if (debouncedSearchQuery.trim().length >= 3 && !selectedTrip) {
      dispatch(
        getGuideTripsSearch(null, {
          q: { title_cont: debouncedSearchQuery.trim() },
          page,
          per_page: pageSize,
          sort_by: "updated_at_desc",
          search: true
        })
      )
    }
  }, [debouncedSearchQuery, dispatch, page, pageSize, confirmedSearchQuery, selectedTrip, i18n])

  // Fetching paginated search results when user has confirmed a search
  useEffect(() => {
    if (confirmedSearchQuery.trim() && !selectedTrip) {
      dispatch(
        getGuideTripsSearch(null, {
          q: {
            title_cont: confirmedSearchQuery.trim()
          },
          page,
          per_page: pageSize,
          sort_by: "updated_at_desc",
          search: false
        })
      )
    }
  }, [confirmedSearchQuery, page, pageSize, dispatch, selectedTrip, i18n])

  // Update selectedTrip with full data from API response
  useEffect(() => {
    if (selectedTrip && searchTrips.length > 0 && !searchLoading) {
      const fullTripData = searchTrips.find((trip) => trip.id === selectedTrip.id)
      if (fullTripData && fullTripData !== selectedTrip) {
        setSelectedTrip(fullTripData)
      }
    }
  }, [searchTrips, searchLoading, selectedTrip, setSelectedTrip])

  const renderSearchResults = useMemo(() => {
    if (!showSearchResults || searchQuery.trim().length < 3) {
      if (searchQuery.trim() && searchQuery.trim().length < 3) {
        return (
          <div
            className={styles.searchResults}
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #d9d9d9",
              zIndex: 1000,
              marginTop: "4px"
            }}
          >
            <div className={styles.noResults}>{t("global.type_at_least_characters", { count: 3 })}</div>
          </div>
        )
      }
      return null
    }

    if (searchLoading) {
      return (
        <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
          <div className={styles.noResults}>{t("loading")}</div>
        </fieldset>
      )
    }

    if (!searchTrips || searchTrips.length === 0) {
      return (
        <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
          <div className={styles.noResults}>{t("global.no_result_found")}</div>
        </fieldset>
      )
    }

    const hasResults = Object.values(categorizedSearchResults).some((list) => list.length > 0)

    if (!hasResults) {
      return (
        <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
          <div className={styles.noResults}>{t("global.no_result_found")}</div>
        </fieldset>
      )
    }

    return (
      <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
        {["publishedTrips", "drafts", "deactivatedTrips"].map(
          (cat) =>
            categorizedSearchResults[cat].length > 0 && (
              <SearchCategory
                key={cat}
                title={t(`experiences.empty_state.${cat.replace("Trips", "")}`)}
                trips={categorizedSearchResults[cat]}
                onTripClick={handleTripClickWithAPI}
                selectedTrip={selectedTrip}
              />
            )
        )}
      </fieldset>
    )
  }, [showSearchResults, searchQuery, searchLoading, searchTrips, categorizedSearchResults, t, handleTripClickWithAPI, selectedTrip])

  return (
    <div className="pb-m">
      <SearchHeader
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchKeyDown={handleSearchKeyDown}
        onClearSearch={handleClearSearchWithReset}
        onAddNewListing={onAddNewListing}
        renderSearchResults={() => renderSearchResults}
      />

      <Flex wrap align="center" justify="space-between" className="mb-m">
        <FilterButtons
          selectedListing={selectedListing}
          setSelectedListing={setSelectedListing}
          t={t}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          activeListingTab={activeListingTab}
          onListingTabChange={handleTabChangeWithReset}
          publishedCount={publishedCount}
          draftCount={draftCount}
          unpublishedCount={unpublishedCount}
          isFilterDropdownOpen={isFilterDropdownOpen}
          onFilterIconClick={handleFilterIconClick}
          onCloseFilterDropdown={handleCloseFilterDropdown}
          FilterDropdown={FilterDropdown}
          pageSize={pageSize}
        />
      </Flex>
      {isCompactDevice || selectedListing === "table" ? (
        <ExperienceGrid
          tripsForMainList={tripsForMainList}
          loading={loading}
          metadata={metadata}
          showPagination={showPagination}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          activeListingTab={activeListingTab}
          onDelete={handleDelete}
          onDeactivate={handleDeactivate}
          onDuplicate={handleDuplicate}
          onShare={handleShare}
          onPublish={handlePublish}
          onEditCategory={handleEditCategory}
          onActionComplete={handleActionComplete}
          onAddNewExperience={handleAddFromEmptyState}
          isDuplicateInProgress={isDuplicateInProgress}
          duplicatingTripId={duplicatingTripId}
        />
      ) : (
        <ListingTable
          onDelete={handleDelete}
          onDeactivate={handleDeactivate}
          onDuplicate={handleDuplicate}
          onShare={handleShare}
          onPublish={handlePublish}
          onEditCategory={handleEditCategory}
          onActionComplete={handleActionComplete}
          onAddNewExperience={handleAddFromEmptyState}
          activeListingTab={activeListingTab}
          data={tripsForMainList}
          loading={loading}
          metadata={metadata}
          showPagination={showPagination}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onEdit={handleEditCategory}
          onView={(record) => console.log("View:", record)}
          onMoreAction={(record) => console.log("More action:", record)}
          isDuplicateInProgress={isDuplicateInProgress}
          duplicatingTripId={duplicatingTripId}
        />
      )}
      <AddNewListing
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        isOpen={isAddNewListingModalOpen}
        onClose={handleCloseModal}
        onContinue={handleContinueModal}
      />
      <EditCategoryModal
        visible={editCategoryModal.visible}
        onClose={handleCloseEditCategoryModal}
        onSave={handleSaveCategory}
        currentCategory={editCategoryModal.trip?.category}
      />
      {modal}
    </div>
  )
}

ExperienceListings.propTypes = {
  onSearch: PropTypes.func
}

ExperienceListings.defaultProps = {
  onSearch: () => {}
}
